import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import type { AssistantPromptExecutionRuntime, AssistantRuntimeRunSummary } from "../types";

type ComposerMode = "send" | "working" | "stop" | "stopping";

interface UseAssistantComposerControlsArgs {
  projectId: Id<"projects">;
  threadId: string | null;
  isSubmittingMessage: boolean;
  hasPendingOptimisticExchange: boolean;
  latestPromptMessageId: string | null;
  promptExecutionStatus: AssistantPromptExecutionRuntime | null | undefined;
  runtimeRun: AssistantRuntimeRunSummary | null;
  onError: (message: string) => void;
}

/**
 * The one place the composer decides what a prompt execution lets it do.
 *
 * `canStop` is the backend's whole answer to what can be stopped; the phase list this used to
 * carry alongside it was a second copy that drifted (a parked question stops, and never
 * appeared in it). `canAddressPromptExecution` is the other half: `stopPromptExecution` is
 * keyed by thread and prompt message, and the runtime snapshot still reports a status for the
 * thread's last execution while the composer has neither. Such an execution is busy, not idle.
 */
function getPromptComposerMode(
  promptExecutionStatus: AssistantPromptExecutionRuntime | null | undefined,
  canAddressPromptExecution: boolean
): ComposerMode {
  if (!promptExecutionStatus) {
    return "send";
  }

  if (promptExecutionStatus.canStop) {
    return canAddressPromptExecution ? "stop" : "working";
  }

  switch (promptExecutionStatus.phase) {
    case "STOP_REQUESTED":
      return "stopping";
    case "RESUME_REQUESTED":
    case "AWAITING_USER_INPUT":
    case "WAITING":
      return "working";
    default:
      return "send";
  }
}

function isPromptExecutionTerminal(
  promptExecutionStatus: AssistantPromptExecutionRuntime | null | undefined
) {
  return (
    promptExecutionStatus?.phase === "STOPPED" ||
    promptExecutionStatus?.phase === "FAILED" ||
    promptExecutionStatus?.phase === "COMPLETED"
  );
}

/**
 * The server-visible half of "this thread is still working": a run the backend has not
 * finished, or a prompt execution that has not reached a terminal phase.
 *
 * Split out of the composer's own liveness so surfaces without the composer's local state —
 * an in-flight submit, an optimistic exchange — can ask the same question of a thread they
 * only subscribe to.
 */
export function hasLiveAssistantWork({
  promptExecutionStatus,
  runtimeRun,
}: {
  promptExecutionStatus: AssistantPromptExecutionRuntime | null | undefined;
  runtimeRun: AssistantRuntimeRunSummary | null | undefined;
}): boolean {
  return Boolean(
    runtimeRun?.status === "CREATED" ||
      runtimeRun?.status === "AWAITING_APPROVAL" ||
      runtimeRun?.status === "AWAITING_USER_INPUT" ||
      runtimeRun?.controls?.canCancel ||
      (promptExecutionStatus && !isPromptExecutionTerminal(promptExecutionStatus))
  );
}

export function useAssistantComposerControls({
  projectId,
  threadId,
  isSubmittingMessage,
  hasPendingOptimisticExchange,
  latestPromptMessageId,
  promptExecutionStatus,
  runtimeRun,
  onError,
}: UseAssistantComposerControlsArgs) {
  const abortRun = useObservedMutation(api.claudia.abortRun, {
    feature: "assistant",
    operation: "abort-run",
    includeRequestId: true,
  });
  const stopPromptExecution = useObservedMutation(api.claudia.stopPromptExecution, {
    feature: "assistant",
    operation: "stop-prompt-execution",
    includeRequestId: true,
  });
  const [optimisticStopQueued, setOptimisticStopQueued] = useState(false);

  const canAddressPromptExecution = Boolean(threadId && latestPromptMessageId);
  const canStopRuntimeResponse = Boolean(
    runtimeRun?.controls?.canCancel || (canAddressPromptExecution && promptExecutionStatus?.canStop)
  );
  const hasActivePromptLifecycle = Boolean(
    hasPendingOptimisticExchange ||
      isSubmittingMessage ||
      (threadId && latestPromptMessageId) ||
      hasLiveAssistantWork({ promptExecutionStatus, runtimeRun })
  );

  useEffect(() => {
    if (!optimisticStopQueued) {
      return;
    }

    if (threadId && latestPromptMessageId && promptExecutionStatus?.canStop) {
      void stopPromptExecution({
        projectId,
        threadId,
        promptMessageId: latestPromptMessageId,
      })
        .catch((cause: unknown) => {
          onError(cause instanceof Error ? cause.message : "Failed to stop assistant response");
        })
        .finally(() => {
          setOptimisticStopQueued(false);
        });
      return;
    }

    if (runtimeRun?.runId && runtimeRun.controls?.canCancel) {
      void abortRun({
        projectId,
        runId: runtimeRun.runId,
      })
        .catch((cause: unknown) => {
          onError(cause instanceof Error ? cause.message : "Failed to stop assistant response");
        })
        .finally(() => {
          setOptimisticStopQueued(false);
        });
      return;
    }

    if (!hasActivePromptLifecycle) {
      setOptimisticStopQueued(false);
    }
  }, [
    abortRun,
    hasActivePromptLifecycle,
    latestPromptMessageId,
    onError,
    optimisticStopQueued,
    projectId,
    promptExecutionStatus?.canStop,
    runtimeRun?.controls?.canCancel,
    runtimeRun?.runId,
    stopPromptExecution,
    threadId,
  ]);

  const mode = useMemo<ComposerMode>(() => {
    const promptMode = getPromptComposerMode(promptExecutionStatus, canAddressPromptExecution);

    if (runtimeRun?.controls?.canCancel) {
      return "stop";
    }
    if (optimisticStopQueued || promptExecutionStatus?.phase === "STOP_REQUESTED") {
      return "stopping";
    }
    // A stoppable prompt outranks the optimistic send window, the way it did when this branch
    // spelled the stop rule out for itself.
    if (promptMode === "stop" || hasPendingOptimisticExchange) {
      return "stop";
    }
    if (isSubmittingMessage) {
      return "working";
    }
    return promptMode;
  }, [
    canAddressPromptExecution,
    hasPendingOptimisticExchange,
    isSubmittingMessage,
    optimisticStopQueued,
    promptExecutionStatus,
    runtimeRun?.controls?.canCancel,
  ]);

  const handleStop = useCallback(async () => {
    try {
      if (threadId && latestPromptMessageId && promptExecutionStatus?.canStop) {
        await stopPromptExecution({
          projectId,
          threadId,
          promptMessageId: latestPromptMessageId,
        });
        return;
      }

      if (runtimeRun?.runId && runtimeRun.controls?.canCancel) {
        await abortRun({
          projectId,
          // LAT-990: the run is cross-checked against this thread server-side, so a stop can
          // never reach a run the composer is no longer showing.
          threadId: threadId ?? undefined,
          runId: runtimeRun.runId,
        });
        return;
      }

      if (hasPendingOptimisticExchange) {
        setOptimisticStopQueued(true);
        return;
      }

      if (hasActivePromptLifecycle) {
        setOptimisticStopQueued(true);
      }
    } catch (cause: unknown) {
      onError(cause instanceof Error ? cause.message : "Failed to stop assistant response");
    }
  }, [
    abortRun,
    hasActivePromptLifecycle,
    hasPendingOptimisticExchange,
    latestPromptMessageId,
    onError,
    projectId,
    promptExecutionStatus?.canStop,
    runtimeRun,
    stopPromptExecution,
    threadId,
  ]);

  return {
    mode,
    handleStop,
    // The stop button stays clickable through the optimistic send window (mode "working" while
    // isSubmittingMessage): clicking queues the stop, which `handleStop` flushes once the run is
    // actually stoppable. Only truly-nothing-to-stop or an in-flight stop disables it.
    isStopDisabled:
      mode === "stopping" ||
      (!canStopRuntimeResponse && !hasPendingOptimisticExchange && !isSubmittingMessage),
  };
}
